import { inject } from 'vue';

import axios from 'axios';

import { cloneDeep, isEqual } from 'lodash';

import { defineStore } from 'pinia';
import { useAuthenticationStore } from 'src/stores/authentication';
import { BatchDelayTasksOneDayCommand } from 'src/stores/task/commands/BatchDelayTasksOneDayCommand';
import { BatchDelayTasksOneWeekCommand } from 'src/stores/task/commands/BatchDelayTasksOneWeekCommand';
import {
	BatchDelayTasksToSpecificDateCommand,
	TaskDelayItem,
} from 'src/stores/task/commands/BatchDelayTasksToSpecificDateCommand';
import { CreateANewLinkedTaskCommand } from 'src/stores/task/commands/CreateANewLinkedTaskCommand';
import { CreateANewTaskCommand } from 'src/stores/task/commands/CreateANewTaskCommand';
import { DelayTheTaskOneDayCommand } from 'src/stores/task/commands/DelayTheTaskOneDayCommand';
import { DelayTheTaskOneWeekCommand } from 'src/stores/task/commands/DelayTheTaskOneWeekCommand';
import { DelayTheTaskToSpecificDateCommand } from 'src/stores/task/commands/DelayTheTaskToSpecificDateCommand';
import { DeleteTheTaskVersionCommand } from 'src/stores/task/commands/DeleteTheTaskVersionCommand';
import { ProcessTaskCommand, type TaskSaveType } from 'src/stores/task/commands/ProcessTaskCommand';
import { TaskQuery } from 'src/stores/task/queries';

import { type ActivityLink } from 'src/models/activityLink';
import { InstanceAuthenticationConfiguration } from 'src/models/authentication';
import { CommandProcessIdHandling } from 'src/models/command';
import { SetTheStateForMultipleAggregates } from 'src/models/status/SetStatusCommand';
import { StatusEnum } from 'src/models/status/enums';
import { Task, TaskRunType } from 'src/models/task';

import { AggregateComparator } from 'src/util/aggregate/AggregateComparator';
import { AggregateMerger } from 'src/util/aggregate/AggregateMerger';
import {
	ApplyTheStatusForTheAggregateIdsPayload,
	aggregateIdWithRemovedIdoAndInstanceId,
	aggregateIdWithUnsetSourceAndTargetContainerId,
	applyTheStatusForTheAggregateIdsInRecordMaps,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { CommandBus } from 'src/util/command/CommandBus';
import { releaseRecord, retainRecord } from 'src/util/records/recordLeases';

import { TaskCallback } from './callbacks';
import {
	AddTaskOriginLinkPayload,
	CreateTheIdoLinkedTaskPayload,
	CreateTheLinkedTaskPayload,
	CreateTheTaskPayload,
	DelayMultipleTasksPayload,
	DelayMultipleTasksToSpecificDatePayload,
	DeleteTheTaskByAggregateIdPayload,
	DeleteTheTaskPayload,
	DeleteTheTaskVersionPayload,
	FetchTaskPayload,
	LinkedTaskCreationResponseBody,
	ReleaseTaskPayload,
	RemoveTaskOriginLinkPayload,
	ResetPayload,
	RetainTaskPayload,
	SaveTheTaskPayload,
	SetTheTaskStatePayload,
	TaskStoreState,
	UnsubscribeFetchTaskPayload,
	UpdateTaskAlarmConfigActivePayload,
	UpdateTheTaskAlarmConfigNotifiableUsersPayload,
	UpdateTheTaskAlarmConfigOwnerTypePayload,
	UpdateTheTaskAlarmDateTimePayload,
	UpdateTheTaskContentPayload,
	UpdateTheTaskFixedMaturityDatePayload,
	UpdateTheTaskRepetitionConfigPayload,
	UpdateTheTaskResponsibleUserIdPayload,
	UpdateTheTaskTitlePayload,
} from './interfaces';
import { type ConfirmedOriginLinksReconciliation } from './types';

const normalizeAggregateId = (aggregateId: string): string =>
	aggregateIdWithUnsetSourceAndTargetContainerId(aggregateId);

let activeTaskStoreOwnerGeneration = 0;

const createDefaultState = () => {
	const taskStoreOwnerGeneration = ++activeTaskStoreOwnerGeneration;

	return {
		confirmedOriginLinksByTask: new Map<string, ConfirmedOriginLinksReconciliation>(),
		initialLoadedTasks: new Map<string, Task>(),
		newIdoTaskAggregateId: '',
		newTaskAggregateId: '',
		taskOriginLinkRemovalGenerations: new Map<string, number>(),
		taskOriginLinkRemovalQueues: new Map<string, Promise<void>>(),
		taskStoreOwnerGeneration,
		tasks: new Map<string, Task>(),
	} satisfies {
		confirmedOriginLinksByTask: Map<string, ConfirmedOriginLinksReconciliation>;
		taskOriginLinkRemovalGenerations: Map<string, number>;
		taskOriginLinkRemovalQueues: Map<string, Promise<void>>;
		taskStoreOwnerGeneration: number;
	} & TaskStoreState;
};

const parseCreatedTaskAggregateId = (result: unknown, stripIdoAndInstanceId: boolean): string => {
	const aggregateId = (
		result as {
			data?: {
				data?: {
					aggregateId?: string;
				};
			};
		}
	).data?.data?.aggregateId;

	if (typeof aggregateId !== 'string') {
		return '';
	}

	if (!stripIdoAndInstanceId) {
		return aggregateId;
	}

	return aggregateIdWithRemovedIdoAndInstanceId(aggregateId);
};

const resolveStateCommandPayload = (
	payload: SetTheTaskStatePayload | string
): { aggregateId: string; commandBus: CommandBus | undefined } => {
	if (typeof payload === 'string') {
		return {
			aggregateId: payload,
			commandBus: inject<CommandBus>('commandBus'),
		};
	}

	return {
		aggregateId: payload.aggregateId,
		commandBus: payload.commandBus ?? inject<CommandBus>('commandBus'),
	};
};

const resolveActiveAuthenticationConfiguration = (): InstanceAuthenticationConfiguration | null =>
	useAuthenticationStore().activeInstanceAuthenticationConfiguration;

const originLinkKey = (originLink: ActivityLink): string =>
	`${originLink.originModuleName}:${String(originLink.originEntryId)}`;

const uniqueOriginLinksForTask = (task: Task): ActivityLink[] => {
	const canonicalOriginLinks = task.activitylinkButton.originLinks;
	const originLinks: ActivityLink[] = Array.isArray(canonicalOriginLinks)
		? canonicalOriginLinks
		: task.activitylinkButton.originEntryId > 0
			? [
					{
						originEntryId: task.activitylinkButton.originEntryId,
						originModuleName: task.activitylinkButton.originModuleName,
					},
				]
			: [];
	const linksByKey = new Map<string, ActivityLink>();

	originLinks.forEach((originLink) => {
		linksByKey.set(originLinkKey(originLink), originLink);
	});

	return Array.from(linksByKey.values());
};

const syncLegacyOriginScalars = (task: Task): void => {
	const [firstOriginLink] = uniqueOriginLinksForTask(task);

	task.activitylinkButton.originLinks = uniqueOriginLinksForTask(task);
	task.activitylinkButton.originEntryId = Number(firstOriginLink?.originEntryId ?? 0);
	task.activitylinkButton.originModuleName = firstOriginLink?.originModuleName ?? '';
};

const applyCanonicalOriginLinks = (task: Task, canonicalOriginLinks: ActivityLink[]): void => {
	const [firstOriginLink] = canonicalOriginLinks;

	task.activitylinkButton.originLinks = cloneDeep(canonicalOriginLinks);
	task.activitylinkButton.originEntryId = Number(firstOriginLink?.originEntryId ?? 0);
	task.activitylinkButton.originModuleName = firstOriginLink?.originModuleName ?? '';
};

const destroyTask = async (
	activeInstanceAuthenticationConfiguration: InstanceAuthenticationConfiguration,
	aggregateId: string,
	saveType: TaskSaveType
): Promise<void> => {
	await axios.post(`${activeInstanceAuthenticationConfiguration.url}/task/task/destroy`, {
		aggregateId: aggregateId,
		saveType: saveType,
	});
};

export const useTaskStore = defineStore('task', {
	actions: {
		addTaskOriginLink(payload: AddTaskOriginLinkPayload): void {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection === undefined || !taskFromCollection.changeable) {
				return;
			}

			const existingOriginLinks = uniqueOriginLinksForTask(taskFromCollection);
			if (
				existingOriginLinks.some(
					(originLink) => originLinkKey(originLink) === originLinkKey(payload.originLink)
				)
			) {
				return;
			}

			taskFromCollection.activitylinkButton.originLinks = [...existingOriginLinks, payload.originLink];
			syncLegacyOriginScalars(taskFromCollection);
			this.tasks.set(normalizeAggregateId(taskFromCollection.aggregateId), taskFromCollection);
		},
		applyConfirmedTaskOriginLinks(payload: { aggregateId: string; canonicalOriginLinks: ActivityLink[] }): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const taskFromCollection = this.tasks.get(normalizedAggregateId);

			if (taskFromCollection === undefined) {
				return;
			}

			applyCanonicalOriginLinks(taskFromCollection, payload.canonicalOriginLinks);
			this.tasks.set(normalizedAggregateId, taskFromCollection);

			const initialLoadedTaskFromCollection = this.initialLoadedTasks.get(normalizedAggregateId);
			if (initialLoadedTaskFromCollection !== undefined) {
				applyCanonicalOriginLinks(initialLoadedTaskFromCollection, payload.canonicalOriginLinks);
				this.initialLoadedTasks.set(normalizedAggregateId, initialLoadedTaskFromCollection);
			}

			this.confirmedOriginLinksByTask.set(normalizedAggregateId, {
				canonicalOriginLinks: cloneDeep(payload.canonicalOriginLinks),
			});
		},
		applyTheStatusForTheAggregateIds(payload: ApplyTheStatusForTheAggregateIdsPayload): void {
			applyTheStatusForTheAggregateIdsInRecordMaps(
				[this.tasks, this.initialLoadedTasks],
				payload.aggregateIds,
				payload.status
			);
		},
		createTheIdoLinkedTask(payload: CreateTheIdoLinkedTaskPayload): void {
			void payload.commandBus
				?.sendCommand(
					new CreateANewTaskCommand(payload.authenticatedUserId, {
						idoWindow: {
							idoWindowIdoInstances: [
								{
									aggregateId: JSON.stringify({
										entryId: 0,
										instanceId: payload.instanceId,
										moduleName: 'module_task',
										sourceContainerId: 0,
										targetContainerId: 0,
										userId: String(payload.authenticatedUserId),
									}),
									aggregateVersion: 0,
									idoWindowIdos: [
										{
											aggregateId: JSON.stringify({
												entryId: 0,
												idoId: payload.idoId,
												instanceId: payload.instanceId,
												moduleName: 'module_task',
												sourceContainerId: 0,
												targetContainerId: 0,
												userId: payload.authenticatedUserId,
											}),
											aggregateVersion: 0,
										},
									],
								},
							],
						},
					})
				)
				?.then((result) => {
					this.setNewIdoTaskAggregateId(parseCreatedTaskAggregateId(result, true));
				});
		},
		createTheLinkedTask(payload: CreateTheLinkedTaskPayload): void {
			void payload.commandBus
				?.sendCommand<LinkedTaskCreationResponseBody>(
					new CreateANewLinkedTaskCommand(payload.authenticatedUserId, payload.activityLinkButton)
				)
				?.then((result) => {
					if (result?.data.success === false) {
						this.setNewTaskAggregateId('');
						payload.onDomainDenial?.('task_create_from_activity_error');
						return;
					}

					this.setNewTaskAggregateId(parseCreatedTaskAggregateId(result, false));
				});
		},
		createTheTask(payload: CreateTheTaskPayload): void {
			void payload.commandBus
				?.sendCommand(
					new CreateANewTaskCommand(payload.authenticatedUserId, {
						idoWindow: { idoWindowIdoInstances: [] },
					})
				)
				?.then((result) => {
					this.setNewTaskAggregateId(parseCreatedTaskAggregateId(result, false));
				});
		},
		delayMultipleTasksOneDay(payload: DelayMultipleTasksPayload): void {
			if (payload.aggregateIds.length > 1) {
				void payload.commandBus?.sendCommand(new BatchDelayTasksOneDayCommand(payload.aggregateIds));
			} else if (payload.aggregateIds.length === 1) {
				void payload.commandBus?.sendCommand(new DelayTheTaskOneDayCommand(payload.aggregateIds[0]));
			}
		},
		delayMultipleTasksOneWeek(payload: DelayMultipleTasksPayload): void {
			if (payload.aggregateIds.length > 1) {
				void payload.commandBus?.sendCommand(new BatchDelayTasksOneWeekCommand(payload.aggregateIds));
			} else if (payload.aggregateIds.length === 1) {
				void payload.commandBus?.sendCommand(new DelayTheTaskOneWeekCommand(payload.aggregateIds[0]));
			}
		},
		delayMultipleTasksToSpecificDate(payload: DelayMultipleTasksToSpecificDatePayload): void {
			if (payload.aggregateIds.length > 1) {
				const taskDelays: TaskDelayItem[] = payload.aggregateIds.map((aggregateId) => ({
					aggregateId,
					targetDate: payload.targetDate,
				}));
				void payload.commandBus?.sendCommand(new BatchDelayTasksToSpecificDateCommand(taskDelays));
			} else if (payload.aggregateIds.length === 1) {
				void payload.commandBus?.sendCommand(
					new DelayTheTaskToSpecificDateCommand(payload.aggregateIds[0], payload.targetDate)
				);
			}
		},
		async deleteTheTask(payload: DeleteTheTaskPayload): Promise<void> {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection === undefined) {
				return;
			}

			const activeInstanceAuthenticationConfiguration = resolveActiveAuthenticationConfiguration();
			if (activeInstanceAuthenticationConfiguration === null) {
				return;
			}

			const task = cloneDeep(taskFromCollection);
			await this.unsubscribeFetchTask({
				aggregateId: task.aggregateId,
				queryStore: payload.queryStore,
			});
			await destroyTask(
				activeInstanceAuthenticationConfiguration,
				task.aggregateId,
				payload.deleteScope === 'all' ? 'withRepetition' : 'withoutRepetition'
			);
		},
		async deleteTheTaskByAggregateId(payload: DeleteTheTaskByAggregateIdPayload): Promise<void> {
			const activeInstanceAuthenticationConfiguration = resolveActiveAuthenticationConfiguration();
			if (activeInstanceAuthenticationConfiguration === null) {
				return;
			}

			await destroyTask(activeInstanceAuthenticationConfiguration, payload.aggregateId, 'withoutRepetition');
		},
		deleteTheTaskVersion(payload: DeleteTheTaskVersionPayload): void {
			void payload.commandBus?.sendCommand(
				new DeleteTheTaskVersionCommand(payload.aggregateId, payload.aggregateVersion, payload.date)
			);
		},
		discardChanges(payload: ResetPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const baseline = this.initialLoadedTasks.get(normalizedAggregateId);
			if (baseline === undefined) {
				return;
			}

			this.tasks.set(normalizedAggregateId, cloneDeep<Task>(baseline));
		},
		fetchTask(payload: FetchTaskPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
				const reconciliation = this.confirmedOriginLinksByTask.get(normalizedAggregateId);
				return payload.queryStore
					.query<Task>({
						callback: new TaskCallback((task) => {
							const activeReconciliation = this.confirmedOriginLinksByTask.get(normalizedAggregateId);
							this.setTask(task);

							if (task !== null && reconciliation !== undefined && activeReconciliation === undefined) {
								const refreshedTask = this.tasks.get(normalizedAggregateId);
								if (refreshedTask !== undefined) {
									applyCanonicalOriginLinks(refreshedTask, reconciliation.canonicalOriginLinks);
									this.tasks.set(normalizedAggregateId, refreshedTask);
								}
								const refreshedBaseline = this.initialLoadedTasks.get(normalizedAggregateId);
								if (refreshedBaseline !== undefined) {
									applyCanonicalOriginLinks(refreshedBaseline, reconciliation.canonicalOriginLinks);
									this.initialLoadedTasks.set(normalizedAggregateId, refreshedBaseline);
								}
							}
						}),
						createTime: new Date(),
						query: new TaskQuery(payload.aggregateId),
						responseType: 'json',
						runType: TaskRunType,
					})
					.finally(() => {
						if (this.confirmedOriginLinksByTask.get(normalizedAggregateId) === reconciliation) {
							this.confirmedOriginLinksByTask.delete(normalizedAggregateId);
						}
					});
			}

			this.setTask(null);
			return Promise.resolve();
		},
		initialLoadedTask(aggregateId: string): Task | undefined {
			return this.initialLoadedTasks.get(normalizeAggregateId(aggregateId));
		},
		markTaskAsSaved(aggregateId: string): void {
			const normalizedAggregateId = normalizeAggregateId(aggregateId);
			const taskFromCollection = this.tasks.get(normalizedAggregateId);
			if (taskFromCollection !== undefined) {
				this.initialLoadedTasks.set(normalizedAggregateId, cloneDeep<Task>(taskFromCollection));
			}
		},
		release(payload: ReleaseTaskPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const wasTheLastLease = releaseRecord(`task:${normalizedAggregateId}`);
			if (!wasTheLastLease || payload.shouldKeepData) {
				return;
			}

			this.taskOriginLinkRemovalGenerations.set(
				normalizedAggregateId,
				(this.taskOriginLinkRemovalGenerations.get(normalizedAggregateId) ?? 0) + 1
			);
			this.confirmedOriginLinksByTask.delete(normalizedAggregateId);
			this.tasks.delete(normalizedAggregateId);
			this.initialLoadedTasks.delete(normalizedAggregateId);
			this.taskOriginLinkRemovalQueues.delete(normalizedAggregateId);
			void this.unsubscribeFetchTask({
				aggregateId: payload.aggregateId,
				queryStore: payload.queryStore,
			});
		},
		removeTaskOriginLink(payload: RemoveTaskOriginLinkPayload): void {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection === undefined || !taskFromCollection.changeable) {
				return;
			}

			taskFromCollection.activitylinkButton.originLinks = uniqueOriginLinksForTask(taskFromCollection).filter(
				(originLink) => originLinkKey(originLink) !== originLinkKey(payload.originLink)
			);
			syncLegacyOriginScalars(taskFromCollection);
			this.tasks.set(normalizeAggregateId(taskFromCollection.aggregateId), taskFromCollection);
		},
		reset(payload: ResetPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const taskOriginLinkRemovalGeneration =
				(this.taskOriginLinkRemovalGenerations.get(normalizedAggregateId) ?? 0) + 1;
			this.taskOriginLinkRemovalGenerations.set(normalizedAggregateId, taskOriginLinkRemovalGeneration);
			this.confirmedOriginLinksByTask.delete(normalizedAggregateId);
			this.tasks.delete(normalizedAggregateId);
			this.initialLoadedTasks.delete(normalizedAggregateId);
			this.taskOriginLinkRemovalQueues.delete(normalizedAggregateId);
			this.newTaskAggregateId = '';
		},
		retain(payload: RetainTaskPayload): void {
			retainRecord(`task:${normalizeAggregateId(payload.aggregateId)}`);
		},
		async saveTheTask(payload: SaveTheTaskPayload): Promise<boolean> {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection === undefined) {
				return false;
			}

			const saveType: TaskSaveType = payload.editScope === 'all' ? 'withRepetition' : 'withoutRepetition';
			const taskForCommand = cloneDeep(taskFromCollection);
			syncLegacyOriginScalars(taskForCommand);
			const response = await payload.commandBus?.sendCommand(new ProcessTaskCommand(taskForCommand, saveType));

			return response !== null && response !== undefined;
		},
		serializeTaskOriginLinkRemoval<Value>(payload: {
			aggregateId: string;
			operation: (isCurrent: () => boolean) => Promise<Value>;
		}): Promise<Value> {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const taskStoreOwnerGeneration = this.taskStoreOwnerGeneration;
			const taskOriginLinkRemovalGeneration =
				this.taskOriginLinkRemovalGenerations.get(normalizedAggregateId) ?? 0;
			const isCurrent = (): boolean =>
				activeTaskStoreOwnerGeneration === taskStoreOwnerGeneration &&
				this.taskStoreOwnerGeneration === taskStoreOwnerGeneration &&
				(this.taskOriginLinkRemovalGenerations.get(normalizedAggregateId) ?? 0) ===
					taskOriginLinkRemovalGeneration;
			const runOperationIfCurrent = (): Promise<Value> =>
				isCurrent() ? payload.operation(isCurrent) : Promise.resolve(undefined as Value);
			const previousOperation = this.taskOriginLinkRemovalQueues.get(normalizedAggregateId);
			const operation =
				previousOperation === undefined
					? runOperationIfCurrent()
					: previousOperation.then(runOperationIfCurrent, runOperationIfCurrent);
			const queueTail = operation.then(
				() => undefined,
				() => undefined
			);

			this.taskOriginLinkRemovalQueues.set(normalizedAggregateId, queueTail);

			return operation.finally(() => {
				if (this.taskOriginLinkRemovalQueues.get(normalizedAggregateId) === queueTail) {
					this.taskOriginLinkRemovalQueues.delete(normalizedAggregateId);
				}
			});
		},
		setNewIdoTaskAggregateId(payload: string): void {
			this.newIdoTaskAggregateId = payload || '';
		},
		setNewTaskAggregateId(payload: string): void {
			this.newTaskAggregateId = payload || '';
		},
		setTask(payload: Task | null): void {
			if (payload === null) {
				return;
			}

			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const reconciliation = this.confirmedOriginLinksByTask.get(normalizedAggregateId);
			if (reconciliation !== undefined) {
				applyCanonicalOriginLinks(payload, reconciliation.canonicalOriginLinks);
				this.confirmedOriginLinksByTask.delete(normalizedAggregateId);
			}
			const taskFromCollection = this.tasks.get(normalizedAggregateId);
			const initialLoadedTaskFromCollection = this.initialLoadedTasks.get(normalizedAggregateId);

			if (taskFromCollection !== undefined && !AggregateComparator.isEqual(taskFromCollection, payload)) {
				return;
			}

			if (
				taskFromCollection !== undefined &&
				initialLoadedTaskFromCollection !== undefined &&
				isEqual(initialLoadedTaskFromCollection, payload)
			) {
				return;
			}

			if (taskFromCollection !== undefined && initialLoadedTaskFromCollection !== undefined) {
				const mergedTask = AggregateMerger.merge(
					initialLoadedTaskFromCollection,
					taskFromCollection,
					payload
				) as Task;
				this.tasks.set(normalizeAggregateId(mergedTask.aggregateId), cloneDeep(mergedTask));
			} else {
				this.tasks.set(normalizedAggregateId, payload);
			}

			this.initialLoadedTasks.set(normalizedAggregateId, cloneDeep(payload));
		},
		setTheTaskStateToReady(payload: SetTheTaskStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.READY
				)
			);
		},
		setTheTaskStateToSeen(payload: SetTheTaskStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.SEEN
				)
			);
		},
		setTheTaskStateToUnseen(payload: SetTheTaskStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.UNSEEN
				)
			);
		},
		task(aggregateId: string): Task | undefined {
			return this.tasks.get(aggregateId);
		},
		async unsubscribeFetchTask(payload: UnsubscribeFetchTaskPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.unsubscribeQuery(new TaskQuery(payload.aggregateId));
			}

			this.setTask(null);
		},
		updateTaskAlarmConfigActive(payload: UpdateTaskAlarmConfigActivePayload): void {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection !== undefined) {
				taskFromCollection.alarmConfig.notifiable = payload.taskAlarmConfigActive;
				this.tasks.set(normalizeAggregateId(taskFromCollection.aggregateId), taskFromCollection);
			}
		},
		updateTheTaskAlarmConfigNotifiableUsers(payload: UpdateTheTaskAlarmConfigNotifiableUsersPayload): void {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection !== undefined) {
				taskFromCollection.alarmConfig.alarmConfigNotifiableUsers = payload.alarmConfigNotifiableUsers;
				this.tasks.set(normalizeAggregateId(taskFromCollection.aggregateId), taskFromCollection);
			}
		},
		updateTheTaskAlarmConfigOwnerType(payload: UpdateTheTaskAlarmConfigOwnerTypePayload): void {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection !== undefined) {
				taskFromCollection.alarmConfig.ownerType = payload.taskAlarmConfigOwnerType;
				this.tasks.set(normalizeAggregateId(taskFromCollection.aggregateId), taskFromCollection);
			}
		},
		updateTheTaskAlarmDateTime(payload: UpdateTheTaskAlarmDateTimePayload): void {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection !== undefined) {
				taskFromCollection.alarmConfig.date = payload.alarmDateTime;
				this.tasks.set(normalizeAggregateId(taskFromCollection.aggregateId), taskFromCollection);
			}
		},
		updateTheTaskContent(payload: UpdateTheTaskContentPayload): void {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection !== undefined) {
				taskFromCollection.content = payload.content;
				this.tasks.set(normalizeAggregateId(taskFromCollection.aggregateId), taskFromCollection);
			}
		},
		updateTheTaskFixedMaturityDate(payload: UpdateTheTaskFixedMaturityDatePayload): void {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection !== undefined) {
				taskFromCollection.fixedMaturityDate = payload.fixedMaturityDate;
				this.tasks.set(normalizeAggregateId(taskFromCollection.aggregateId), taskFromCollection);
			}
		},
		updateTheTaskRepetitionConfig(payload: UpdateTheTaskRepetitionConfigPayload): void {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection !== undefined) {
				taskFromCollection.repetitionConfig = {
					...taskFromCollection.repetitionConfig,
					...payload.taskRepetitionConfigPatch,
				};
				this.tasks.set(normalizeAggregateId(taskFromCollection.aggregateId), taskFromCollection);
			}
		},
		updateTheTaskResponsibleUserId(payload: UpdateTheTaskResponsibleUserIdPayload): void {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection !== undefined) {
				taskFromCollection.responsibleUserId = payload.responsibleUserId;
				this.tasks.set(normalizeAggregateId(taskFromCollection.aggregateId), taskFromCollection);
			}
		},
		updateTheTaskTitle(payload: UpdateTheTaskTitlePayload): void {
			const taskFromCollection = this.tasks.get(normalizeAggregateId(payload.aggregateId));

			if (taskFromCollection !== undefined) {
				taskFromCollection.title = payload.title.replace(/\n/g, ' ');
				this.tasks.set(normalizeAggregateId(taskFromCollection.aggregateId), taskFromCollection);
			}
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
